import { Category } from '@blisspointmedia/bpm-types/dist/FilterPane';
import { CDN } from '@Components/MetricsTable/metrics-table-utils';
import { Channel1, Channel2, Channel5 } from '@src/utils/colors';
import {
  computeCustomRelativeRange,
  DateRange
} from '@blisspointmedia/bpm-types/dist/RelativeDatePresets';

export const DiversityLabel = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH'
} as const;
export type DiversityLabelType = (typeof DiversityLabel)[keyof typeof DiversityLabel];

export interface FatiguePoint {
  date: string;
  fatigue: number;
}

export interface PerfDailyPoint {
  date: string;
  spend: number;
  roas: number | null;
  cpx: number | null;
  ctr: number | null;
  cpm: number | null;
}

export const PlatformTier = {
  BEST: 'BEST',
  GOOD: 'GOOD',
  LOW: 'LOW'
} as const;
export type PlatformTierType = (typeof PlatformTier)[keyof typeof PlatformTier];

export interface PlatformSpread {
  tier: PlatformTierType;
  publishers: string[]; // distinct publisher_platform values with observed delivery
  surfaces: string[]; // distinct surface kinds: feed / story / video
  placementCount: number; // distinct (publisher, position) combos with observed delivery
}

export interface AdSetStatus {
  status: string;
  effectiveStatus: string;
  active: boolean;
}

export interface LaunchEvent {
  date: string;
  count: number;
  adIds: string[];
}

export interface Ad {
  adId: string;
  name: string;
  format: string;
  spend: number;
  impressions: number;
  clicks: number;
  roas: number | null;
  cpm: number | null;
  ctr: number | null;
  cpx: number | null;
  cpc: number | null;
  kpiVolume: number | null;
  kpiRevenue: number | null;
  // Share of the ad set's spend. Recomputed against the VISIBLE ads when an ad-level
  // filter prunes the list (see recomputeAdSetFromAds in meta-fatigue-insights-utils.ts),
  // so this is a share of what is on screen, not of the untouched account.
  spendShare: number;
  launchDate: string | null;
}

export interface FatiguedAd {
  adObjectId: string;
  name: string;
  format: string;
  formatCode: number | null;
  spend: number;
  costPerResult: number | null;
  thumbnailUrl: string | null;
}

export interface AdSet {
  adSetId: string;
  name: string;
  campaign: string;
  campaignIndex: number;
  objective: string;
  account: string;
  accountName: string;
  spend: number;
  impressions: number;
  clicks: number;
  roas: number | null;
  cpm: number | null;
  ctr: number | null;
  cpx: number | null;
  cpc: number | null;
  kpiVolume: number | null;
  kpiRevenue: number | null;
  diversityLabel: DiversityLabelType | null;
  diversityScore: number | null;
  // activeAds / creativesDriving / spendShare all describe this ad set's ad list, so all
  // three are recomputed when an ad-level filter prunes that list. See
  // recomputeAdSetFromAds in meta-fatigue-insights-utils.ts.
  activeAds: number;
  creativesDriving: number;
  spendShare: number;
  fatigueLatest: number | null;
  fatigueSeries: FatiguePoint[];
  fatigueFlagged: boolean;
  costPerResult: number | null;
  platforms: PlatformSpread | null;
  status: AdSetStatus | null;
  perfDaily: PerfDailyPoint[];
  launches: LaunchEvent[];
  ads: Ad[];
}

// View-model aliases so component prop types read naturally. Every ad set the page
// shows is a real ad set; the tabs differ only in which subset they render.
//
// DiversityAdSet narrows AdSet to the ad sets that actually have a usable diversity
// reading — getDiversityAdSets (meta-fatigue-insights-utils.ts) is what produces this
// narrowing at runtime. FatigueAdSet stays a plain AdSet alias: fatigue never depended
// on diversity, so every tracked ad set (NA-diversity or not) qualifies.
export type DiversityAdSet = AdSet & { diversityLabel: DiversityLabelType; diversityScore: number };
export type FatigueAdSet = AdSet;
export type FatigueAd = Ad;

export const Tab = {
  COMBINED: 'combined',
  DIVERSITY: 'diversity',
  FATIGUE: 'fatigue'
} as const;
export type TabType = (typeof Tab)[keyof typeof Tab];

// Header nav pills (standard Page nav structure, URL-synced via useTabbedNav).
export const TAB_NAVS: { key: TabType; label: string }[] = [
  { key: Tab.COMBINED, label: 'Combined view' },
  { key: Tab.DIVERSITY, label: 'Diversity' },
  { key: Tab.FATIGUE, label: 'Fatigue' }
];

export const DEFAULT_DATE_RANGE = computeCustomRelativeRange(
  { number: 30, interval: 'Days', before: true, referenceDate: 'Yesterday' },
  { number: 0, interval: 'Days', before: true, referenceDate: 'Yesterday' },
  {}
) as DateRange;

// Filter presets are stored per (company, platform), so this value is what scopes this
// page's saved presets to this page. It deliberately does NOT match the social
// single-channel page's platform value: presets record category KEYS, and the two pages
// key their categories differently — social uses database column names
// (`campaign_name`, `ad_group_name`, `ad_name`) while buildFilterCategories below uses
// short names (`campaign`, `adSet`, `ad`). A social preset loaded here would therefore
// find none of its keys, and `filterAdSets` treats an absent key as "no limit", so the
// preset would load without filtering anything — and `hasFilter` would still report a
// filter as active. Keeping the two sets apart avoids that silent mismatch entirely.
// Meta's write-up of how the diversity score is calculated, opened from the metrics guide.
//
// Served from the CDN rather than committed to the repo, so the document can be replaced
// without a frontend deploy. Note this is the `cdn.blisspointmedia.com` host, NOT the raw
// `bpm-cdn.s3.us-west-2.amazonaws.com` object URL: the bucket object is not publicly
// readable over the S3 endpoint (it answers 403), while the CDN serves the same object
// with `Content-Type: application/pdf`, which is what lets the browser render it inline in
// the modal's iframe instead of downloading it. Same host the Geist fonts load from in
// styles/font-faces.scss.
export const DIVERSITY_PNG_URL = `${CDN}/metaCreativeHealthDashboard/meta-diversity-score-overview.png`;

// Meta's own help-centre article on creative fatigue, linked from the fatigue-rubric
// bullet in the metrics guide. External and public, so it is a plain anchor rather than
// anything that needs fetching or signing.
export const META_FATIGUE_HELP_URL =
  'https://business.facebook.com/business/help/1346816142327858?id=561906377587030';

export const FILTER_PLATFORM = 'meta-creative-health';

// DEMO_COMPANY gets generated demo data from the lambdas (bpm-lambdas/meta/metaFatigueDemo.ts,
// which checks the same company). Other test companies get their real data. The constants below
// cover the two things that don't come from those lambdas: the demo ads have no real thumbnails or
// Meta previews, so every ad shows this placeholder (key in CREATIVE_BUCKET_NAME); and the KPI
// picker shows these generic KPIs rather than the company's configured ones, which can name real
// clients. The demo endpoint accepts any KPI.
export const DEMO_COMPANY = 'tinuititest';
export const DEMO_THUMBNAIL_KEY = 'creativeAssets/metaThumbnails/test/placeholder.jpg';
export const DEMO_KPIS = ['Purchases', 'Sign-ups', 'Add to cart'];

// Mirrors FetchFilterBar's `filterSource` union. The shared component types that prop as an
// inline string-literal union and exports no name for it, so this gives the page named
// constants to pass instead of bare strings (repo style guide: "Prefer defined constants
// over string literal unions"). The derived type is structurally identical to the union the
// component expects, so it is assignable without a cast.
export const FilterSource = {
  PRESET_FILTER_ID: 'presetFilterID',
  CURRENT_FILTER_ID: 'currentFilterID',
  PRESET_STATE: 'presetState',
  CURRENT_STATE: 'currentState'
} as const;
export type FilterSourceType = (typeof FilterSource)[keyof typeof FilterSource];

const unique = (values: string[]): string[] => [...new Set(values)].sort();

// Every option is derived from data the page already holds — there is no /getFilterOptions
// fetch behind this bar. That is also the ceiling on what can be filtered: a field no
// lambda returns cannot become a category here.
//
// "Creative type" used to sit between Ad set and Diversity. It was removed rather than
// carried forward: every ad is built with the hardcoded format 'Static' (see
// meta-fatigue-data.ts), so the category could never tell two ads apart. Restore it when
// a real creative-format source exists.
export const buildFilterCategories = (adSets: AdSet[], campaigns: string[]): Category[] => [
  { label: 'Account', key: 'account', options: unique(adSets.map(adSet => adSet.accountName)) },
  { label: 'Campaign', key: 'campaign', options: unique(campaigns) },
  { label: 'Ad set', key: 'adSet', options: unique(adSets.map(adSet => adSet.name)) },
  {
    label: 'Ad',
    key: 'ad',
    options: unique(adSets.flatMap(adSet => adSet.ads.map(ad => ad.name)))
  },
  {
    label: 'Diversity',
    key: 'diversity',
    options: [DiversityLabel.LOW, DiversityLabel.MEDIUM, DiversityLabel.HIGH]
  }
];

export const ColorMode = {
  CPX: 'cpx',
  CAMPAIGN: 'campaign'
} as const;
export type ColorModeType = (typeof ColorMode)[keyof typeof ColorMode];

export const COLOR_CPX_OPTIONS = [
  { key: ColorMode.CPX, label: 'CPX' },
  { key: ColorMode.CAMPAIGN, label: 'Campaign' }
] as const;

// The three series on the isolated-ad-set focus chart. Single source of truth for both
// the chart strokes (FocusChart) and the legend swatches (CombinedPanel) so the legend
// can never drift from the lines it describes.
//
// LineChart only has two y-axes (left/right), so with three series something has to share.
// CTR pairs with fatigue on 'rate' (left) — both are bounded ~0-1 fractions — rather than
// with CPX on 'cost' (right), which is an unbounded $ figure often 1-4+ orders of magnitude
// larger than CTR; sharing that axis flattened CTR to a near-invisible line at the bottom.
export const FOCUS_SERIES = [
  { key: 'fatigue', label: 'Fatigue', color: Channel5, axisId: 'rate' },
  { key: 'cpx', label: 'CPX', color: Channel2, axisId: 'cost' },
  { key: 'ctr', label: 'CTR', color: Channel1, axisId: 'rate' }
] as const;

// ── Alerts ───────────────────────────────────────────────────────────────────
// Real data only supports diversity alerts (LOW-label ad sets). Fatigue alerts are
// intentionally absent: there is no agreed fatigue threshold on this metric's scale
// yet, so nothing is flagged as "fatigued" (see this page's CLAUDE.md).
export const AlertCategory = {
  DIVERSITY: 'diversity'
} as const;
export type AlertCategoryType = (typeof AlertCategory)[keyof typeof AlertCategory];

export interface Alert {
  id: string;
  category: AlertCategoryType;
  summary: string;
  adSets: DiversityAdSet[];
}

// ── Quadrant ───────────────────────────────────────────────────────────────
export interface QuadrantPoint {
  id: string;
  name: string;
  x: number; // diversity score (0–1), or 0 when imputed — see imputedX
  y: number; // latest fatigue (0–1), or 0 when imputed — see imputedY
  spend: number; // drives bubble size
  cpx: number | null;
  camp: number; // campaign index
  // null when this ad set has no usable diversity reading. The quadrant plots such an ad
  // set at x = 0, so the label cannot be assumed present the way it once could.
  label: DiversityLabelType | null;
  // True when the matching axis value was MISSING and substituted with 0, rather than
  // being a real 0. The distinction matters: a real diversity score of 0 is meaningful
  // data (the metrics guide says so explicitly), and an imputed 0 is the absence of data
  // at the same coordinate. Both flags drive the bubble outline and the tooltip so the
  // two never read as the same thing. An ad set with BOTH values missing is not plotted.
  imputedX: boolean;
  imputedY: boolean;
}
